import { getInstanceDefinition } from '../cards/cardLookup';
import { getHandler } from '../cards/effectRegistry/index';
import { computeBaseAttack, DEFEAT_CONDITIONS } from './attackMath';
import { computeTotalLight, computeDarknessPenalty, hasDarknessAtRank } from './lightDarkness';
import { compactAndRefill } from '../dungeon/hall';
import {
  applyInstanceOverride, isHeroLike, hasTraitPresent, consumeCircleOfProtection, shiftHallCardToRank, gainCurse,
  anyLastDoomknightInHall, applyLastDoomknightHeroStrengthPenalty, computeMonsterLightPenalty, markInstanceIgnored,
  destroyFromHand, discardFromHand, grantTemporaryStrength, grantXp,
} from '../cards/effectHelpers';
import { dropDeadEntries } from '../actions/handChoiceActions';
import { getAssignedMonsterInstanceId } from '../state/selectors';

// Belac (bearer-belac) has a Global effect: "Resolve all other monsters'
// Battle effects twice." Rather than build a generic Global-effect framework
// for what is, in the ToR card pool, exactly one card with this shape, it's
// special-cased here: if Belac is anywhere in the hall (not necessarily the
// monster being fought), every Battle handler call below runs an extra time.
function isBelacPresentInHall(state) {
  return state.dungeonHall.some((instanceId) => {
    if (!instanceId) return false;
    return getInstanceDefinition(state, instanceId).id === 'bearer-belac';
  });
}

// Into the Abyss, Blind Shihan (L4): "specialEffects: ...and ignore Battle
// effects if no Light items are present." A hand-wide condition, same
// "no Light items" check attackMath.js's own Magic Attack bonus for this
// same card (and its L3 Grandmaster) already uses. Checked once per fought
// monster, at the same outer choke point Veilminder's own cancellation
// uses (isCancelled below) rather than inside every individual Battle
// handler - correct for the overwhelming majority of Battle effects
// (single-player-targeted, only ever affecting whoever chose to fight),
// but a known, narrow over-reach for the handful of "each player..."
// Battle effects (Charred Bruin, Blazing Grimalkin, Hellhound, Pyre Viper,
// Shrouded Cadaver): skipping the whole handler call also exempts OTHER
// players from their own share of that same effect whenever the Blind-
// Shihan-holder is the one who fought this monster, not just the holder
// themselves. Reasonable default given how rarely both conditions
// coincide; a fully precise version would need this same check duplicated
// inside each of those 5 handlers individually.
function hasBlindShihanBattleImmunity(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  const hasShihan = player.hand.some((id) => getInstanceDefinition(state, id).id === 'hero-blind-l4');
  if (!hasShihan) return false;
  return !player.hand.some((id) => {
    const t = getInstanceDefinition(state, id).traits || [];
    return t.includes('Item') && t.includes('Light');
  });
}

// Battle/Aftermath effects can be pre-cancelled for a specific monster
// instance by Veilminder's Dungeon ability (see effectRegistry/heroes.js) -
// checked here rather than at the Veilminder-ability call site, since the
// cancellation is declared before the monster is even chosen to fight.
function isCancelled(state, playerId, timing, monsterInstanceId) {
  if (timing !== 'battle' && timing !== 'aftermath') return false;
  const player = state.players.find((p) => p.id === playerId);
  if (player.turnFlags.allBattleAftermathCancelled) return true;
  if (timing === 'battle' && hasBlindShihanBattleImmunity(state, playerId)) return true;
  const list = timing === 'battle'
    ? player.turnFlags.cancelledBattleInstanceIds
    : player.turnFlags.cancelledAftermathInstanceIds;
  return list.includes(monsterInstanceId);
}

// Root of Corruption, Circle of Protection: "Cancel the next Raid, Breach,
// or Aftermath effect that takes place on your turn and destroy this
// card." Only the Aftermath half is checked here (Battle is not one of the
// 3 listed timings) - right before a handler is actually about to run, so a
// monster with no registered Aftermath handler never burns a copy for
// nothing. repeatCount only ever applies to Battle (Belac/Doomladen
// doubling - see resolveCombat's own callers below), never Aftermath, so
// there's no ambiguity about whether a cancellation should also skip
// multiple reps here.
function resolveTimedHandlers(state, def, timing, ctx, repeatCount = 1) {
  if (isCancelled(state, ctx.playerId, timing, ctx.monsterInstanceId)) return state;
  // Into the Abyss, Icon of Inversion: "Battle effects become Aftermath
  // effects" (turnFlags.battleEffectsBecomeAftermath - see turnFlags.js's
  // own comment). A Battle-timed handler is queued instead of run now;
  // flushDeferredBattleEffects below runs it for real once win/loss is
  // already decided. Gated on `timing === 'battle'` only - cancellation is
  // still checked above at ORIGINAL (Battle) timing, matching this card's
  // own text ("Battle effects" still ARE Battle effects for cancellation
  // purposes, just resolved later).
  const player = state.players.find((p) => p.id === ctx.playerId);
  if (timing === 'battle' && player?.turnFlags.battleEffectsBecomeAftermath) {
    let nextState = state;
    (def.abilityText[timing] || []).forEach((entry, abilityIndex) => {
      if (!getHandler(def.id, timing, abilityIndex)) return; // not yet implemented - silently skipped
      nextState = updatePlayerTurnFlags(nextState, ctx.playerId, (tf) => ({
        ...tf,
        deferredBattleEffects: [...tf.deferredBattleEffects, {
          defId: def.id, abilityIndex, instanceId: ctx.instanceId, monsterInstanceId: ctx.monsterInstanceId, hallRankIndex: ctx.hallRankIndex, repeatCount,
        }],
      }));
    });
    return nextState;
  }
  let nextState = state;
  (def.abilityText[timing] || []).forEach((entry, abilityIndex) => {
    const handler = getHandler(def.id, timing, abilityIndex);
    if (!handler) return; // not yet implemented - silently skipped
    if (timing === 'aftermath') {
      const { state: afterCancel, cancelled } = consumeCircleOfProtection(nextState, ctx.playerId);
      nextState = afterCancel;
      if (cancelled) return;
    }
    for (let i = 0; i < repeatCount; i += 1) {
      nextState = handler(nextState, { ...ctx, choices: {} });
    }
  });
  return nextState;
}

function updatePlayerTurnFlags(state, playerId, updateFn) {
  return {
    ...state,
    players: state.players.map((p) => (p.id === playerId ? { ...p, turnFlags: updateFn(p.turnFlags) } : p)),
  };
}

// Flushes turnFlags.deferredBattleEffects (see Icon of Inversion above),
// actually running each queued Battle-timed handler now that win/loss is
// already decided - called alongside the fought monster's own Aftermath
// resolution, once totalAttack/won are known.
function flushDeferredBattleEffects(state, playerId) {
  const queued = state.players.find((p) => p.id === playerId).turnFlags.deferredBattleEffects;
  if (!queued.length) return state;
  let nextState = updatePlayerTurnFlags(state, playerId, (tf) => ({ ...tf, deferredBattleEffects: [] }));
  for (const entry of queued) {
    const handler = getHandler(entry.defId, 'battle', entry.abilityIndex);
    if (!handler) continue;
    for (let i = 0; i < entry.repeatCount; i += 1) {
      nextState = handler(nextState, {
        playerId, instanceId: entry.instanceId, monsterInstanceId: entry.monsterInstanceId, hallRankIndex: entry.hallRankIndex, choices: {},
      });
    }
  }
  return nextState;
}

// Orseg (bearer-orseg): "Orseg gains the Battle effects of each other
// monster in the dungeon hall." Fighting Orseg also resolves every OTHER
// monster currently in the hall's own Battle effect(s), as part of this same
// combat - same repeatCount as Orseg's own text, since Belac's "resolve all
// other monsters' Battle effects twice" is a blanket aura on Battle
// resolution generally, not just on the fought monster's own printed text.
function resolveOrsegBorrowedBattleEffects(state, monsterDef, ctx, repeatCount) {
  if (monsterDef.id !== 'bearer-orseg') return state;
  let nextState = state;
  state.dungeonHall.forEach((otherInstanceId, otherRank) => {
    if (!otherInstanceId || otherRank === ctx.hallRankIndex) return;
    const otherDef = getInstanceDefinition(state, otherInstanceId);
    nextState = resolveTimedHandlers(nextState, otherDef, 'battle', {
      ...ctx, instanceId: otherInstanceId, monsterInstanceId: otherInstanceId,
    }, repeatCount);
  });
  return nextState;
}

// Numenera, Convergence Master (bearer-convergence-master): "This card
// gains the Battle ability on each card present (in your hand)." Same
// shape as Orseg's own "gains the Battle effects of each other monster in
// the hall" just above - sourced from the fighting player's HAND instead of
// the rest of the hall. Battle is normally a monster-only timing keyword,
// and no hero/village/villager card anywhere in this engine's card pool
// (ToR/CoB/RoC/Numenera/Start/ItA) ever prints Battle-timed text - verified
// against every scripts/source-data file - so this currently resolves to a
// no-op for every real game, exactly like the printed text implies it
// should when nothing present happens to have one. Built as the genuine,
// general mechanism anyway (not hardcoded to do nothing) so it would
// automatically pick up a future card if one ever printed Battle text on a
// hand-card type, matching this codebase's "primitives over bespoke"
// convention.
function resolveConvergenceMasterBorrowedHandBattleEffects(state, monsterDef, ctx, repeatCount) {
  if (monsterDef.id !== 'bearer-convergence-master') return state;
  const player = state.players.find((p) => p.id === ctx.playerId);
  let nextState = state;
  for (const instanceId of player.hand) {
    const def = getInstanceDefinition(state, instanceId);
    nextState = resolveTimedHandlers(nextState, def, 'battle', {
      ...ctx, instanceId, monsterInstanceId: instanceId,
    }, repeatCount);
  }
  return nextState;
}

// Rulebook, "Aftermath Effects": "Check to see if the monster you fought (or
// any hero or village card present) has any Aftermath effects. If so, you
// must apply them... Do not check Aftermath effects of monster cards in
// your hand." No ToR card ever exercised the "hero or village card"
// half of this (none of them print Aftermath text), so it was never
// implemented - only the fought monster's own Aftermath was ever resolved.
// Surfaced by Caverns of Bane's Disowned Berserker ("Aftermath: Destroy 1
// other hero."). NOT routed through resolveTimedHandlers/isCancelled above -
// this is a SEPARATE dispatch loop (hand cards' own Aftermath text, not the
// fought monster's), so per-monster cancellation (Veilminder L1/L2's
// "cancel one Battle or Aftermath effect ON A MONSTER in the hall",
// cancelledAftermathInstanceIds) correctly can't reach it - there's no hall
// monster instance to target here.
//
// Matt (relaying a friend's report): "The Veilminder Priest is able to
// cancel all battle and aftermath effects... We should make sure that the
// veilminder's cancel is generally applicable. It's not just about Belac."
// Priest (L3)'s own printed text has no "on a monster" qualifier at all -
// "Discard this card to cancel all Battle and Aftermath effects" - so
// unlike L1/L2, its blanket cancellation SHOULD also reach hand-card
// Aftermaths like Disowned Berserker's. That's the allBattleAftermathCancelled
// check below; nothing else in this loop is Priest-scoped.
function resolveHandCardAftermaths(state, ctx) {
  const { playerId } = ctx;
  if (state.players.find((p) => p.id === playerId)?.turnFlags.allBattleAftermathCancelled) return state;
  const startingHand = state.players.find((p) => p.id === playerId).hand;
  let nextState = state;
  for (const instanceId of startingHand) {
    const currentPlayer = nextState.players.find((p) => p.id === playerId);
    if (!currentPlayer.hand.includes(instanceId)) continue; // an earlier hand-card Aftermath already removed it
    const def = getInstanceDefinition(nextState, instanceId);
    if (def.type === 'monster') continue; // rulebook exception - monster cards in hand don't trigger
    (def.abilityText.aftermath || []).forEach((entry, abilityIndex) => {
      const handler = getHandler(def.id, 'aftermath', abilityIndex);
      if (!handler) return; // not yet implemented - silently skipped, same convention as resolveTimedHandlers
      // Circle of Protection: same cancellation as resolveTimedHandlers'
      // own Aftermath check above - this is a SEPARATE dispatch loop (hand
      // cards' own Aftermath text, not the fought monster's), so it needs
      // its own check right before this handler actually runs.
      const { state: afterCancel, cancelled } = consumeCircleOfProtection(nextState, playerId);
      nextState = afterCancel;
      if (cancelled) return;
      nextState = handler(nextState, { ...ctx, instanceId, choices: {} });
    });
  }
  return nextState;
}

// village-smuggler is the only React card in the ToR pool ("Destroy this card
// after a player loses a combat. Buy 1 card."). React abilities are OPTIONAL
// (rulebook, "Abilities": "you choose whether or not to use them" - applies
// uniformly to Village/Dungeon/React/Spoils; confirmed against the
// rulebook's own worked Smuggler example, where the player actively
// "shows the cards to everyone" and chooses to resolve it) - so this can't
// auto-fire the way it used to. Modeled the same way Spoils abilities are
// (meta.spoilsWindowOpen, opened here / closed by endTurn or the next
// fight): meta.smugglerReactWindowOpen opens whenever a combat is LOST with
// an eligible Smuggler in hand, and actions/abilityActions.js's
// useSmugglerReact resolves it as its own dispatched action (letting the
// player genuinely choose which card to buy, the same way Spoils-buy
// abilities do) rather than baking a forced choice into this same dispatch.
// Full cross-player React support (another player's Smuggler reacting to
// *your* combat) would need a generic trigger-broadcast system for one
// card; simplified here to the acting player's own Smuggler only.
function hasEligibleSmuggler(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand.some((id) => getInstanceDefinition(state, id).id === 'village-smuggler');
}

// Drakeclan Ambusher/Cutter/Shaman: "When you defeat this monster, you may
// place it into any player's discard pile." Defaults to the acting player's
// own discard pile (declining the redirect) unless choices.discardToPlayerId
// names a valid player. Exported so the UI (DungeonHall.vue) can ask the
// player *before* fighting one of these - the redirect choice only matters
// on a win, but choices must be supplied up front (dispatch() is
// synchronous/pure, it can't pause mid-resolution to ask), and a redirect
// answer is simply ignored below if the fight is actually lost, so asking
// unconditionally before every fight against one of these is safe.
export const DEFEAT_REDIRECTABLE = new Set(['monster-drakeclan-ambusher', 'monster-drakeclan-cutter', 'monster-drakeclan-shaman']);

// Caverns of Bane, every Troll: "Does not leave the hall after a player
// loses a combat against this card." Functionally identical to a Guardian's
// "never retreat" on a loss (the only branch that matters - Trolls aren't
// otherwise printed with the Guardian trait, and this text says nothing
// about staying after a WIN, which already removes it normally).
const STAYS_IN_HALL_ON_LOSS = new Set([
  'monster-troll-skullcracker', 'monster-troll-osteomancer', 'monster-troll-bonebreaker', 'monster-troll-marrowgaunt', 'monster-troll-spinecutter',
]);

// Into the Abyss, Steam Torment: "Global: Monsters will not leave the hall
// unless defeated." A THIRD reason a lost fight's monster stays put,
// alongside Guardians/Trolls just above - but this one is a whole-hall-
// state passive gated on ANY Steam Torment being present anywhere in the
// hall (not just the monster actually fought), so it's checked separately
// rather than folded into STAYS_IN_HALL_ON_LOSS (which is keyed on the
// FOUGHT monster's own id).
function anySteamTormentInHall(state, hall) {
  return hall.some((id) => id && getInstanceDefinition(state, id).id === 'monster-steam-torment');
}

// Into the Abyss, Plaguebearer: "Global: Each player gains 1 disease when
// another monster is defeated." Checked against the hall AFTER this fight's
// own removal is applied (see the resolveCombat call site) - a lone
// Plaguebearer that was itself just defeated is already gone from that
// array by then, so it never triggers off its own defeat, while a second
// copy elsewhere in the hall still would.
function anyPlaguebearerInHall(state, hall) {
  return hall.some((id) => id && getInstanceDefinition(state, id).id === 'monster-plaguebearer');
}

// Resolves the fought monster's own Battle effect (plus Belac/Doomladen
// doubling and Orseg/Convergence-Master borrowed Battle effects) and stops
// there. Split out of the old single-function resolveCombat specifically so
// win/loss and Aftermath (resolveCombatContinuePhase below) can be paused
// until any player choice Battle just queued (queueHandChoice et al.) is
// actually answered - see resolveCombat's own comment for why. Returns
// { state, ctx } where ctx carries everything resolveCombatContinuePhase
// needs to pick up where this left off.
function resolveCombatBattlePhase(state, { playerId, hallRankIndex, choices }) {
  const isAssignedFight = hallRankIndex === -1;
  const monsterInstanceId = isAssignedFight
    ? getAssignedMonsterInstanceId(state, playerId)
    : state.dungeonHall[hallRankIndex];
  if (!monsterInstanceId) {
    throw new Error(isAssignedFight ? 'No monster is currently assigned to you.' : `No monster present at hall rank ${hallRankIndex + 1}`);
  }

  // Numenera, Sathosh: "After you select this monster to fight, move it to
  // rank 3." Happens immediately on selection, before Battle/attack/Darkness
  // are computed - the fight itself takes place from Sathosh's NEW position,
  // not wherever it was originally clicked. Reassigning hallRankIndex here
  // (rather than tracking "original" vs "new" separately) means every
  // rank-dependent calculation below - Darkness, Battle ctx, the win/loss
  // hall-write-back, even the rank stashed onto meta.lastCombatResult -
  // automatically uses the post-move position with no further
  // special-casing. Index 2 ("rank 3") is a literal constant from the
  // printed text, not "the highest rank" (contrast Rockslide's own move,
  // dungeon/hall.js) - correct regardless of board side.
  if (!isAssignedFight && getInstanceDefinition(state, monsterInstanceId).id === 'monster-sathosh' && hallRankIndex !== 2) {
    state = shiftHallCardToRank(state, hallRankIndex, 2);
    hallRankIndex = state.dungeonHall.indexOf(monsterInstanceId);
  }

  const monsterDefBeforeBattle = getInstanceDefinition(state, monsterInstanceId);

  // Numenera, Disassembler/Mastigophore/Dark Fathom: "Resolve this card's
  // Battle effect twice if a Doomladen is present (in your hand)." A
  // conditional doubling (unlike Belac's own blanket, always-on doubling) -
  // combined multiplicatively in the rare case both apply at once.
  const DOOMLADEN_DOUBLES_BATTLE = new Set(['monster-disassembler', 'monster-mastigophore', 'monster-dark-fathom']);
  const doomladenDoubles = DOOMLADEN_DOUBLES_BATTLE.has(monsterDefBeforeBattle.id)
    && hasTraitPresent(state, playerId, 'Doomladen');
  const battleRepeatCount = (isBelacPresentInHall(state) && monsterDefBeforeBattle.id !== 'bearer-belac' ? 2 : 1)
    * (doomladenDoubles ? 2 : 1);
  let workingState = resolveTimedHandlers(state, monsterDefBeforeBattle, 'battle', {
    playerId, instanceId: monsterInstanceId, monsterInstanceId, hallRankIndex,
  }, battleRepeatCount);
  workingState = resolveOrsegBorrowedBattleEffects(
    workingState, monsterDefBeforeBattle, { playerId, hallRankIndex }, battleRepeatCount,
  );
  workingState = resolveConvergenceMasterBorrowedHandBattleEffects(
    workingState, monsterDefBeforeBattle, { playerId, hallRankIndex }, battleRepeatCount,
  );

  return {
    state: workingState,
    ctx: {
      playerId, hallRankIndex, isAssignedFight, monsterInstanceId, choices,
    },
  };
}

// Everything after the fought monster's own Battle effect: win/loss and
// Aftermath, through hall/XP/chain-kill bookkeeping. Takes over exactly
// where resolveCombatBattlePhase left off - see resolveCombat below for how
// the two are stitched together (immediately, or after a pause).
function resolveCombatContinuePhase(workingState, {
  playerId, hallRankIndex, isAssignedFight, monsterInstanceId, choices,
}) {
  // Re-read rather than threaded through ctx from the Battle phase - purely
  // for definitional lookups below (which monster this is), same "always
  // re-read off current state" convention as monsterDef's own re-read a
  // few lines down (whose comment explains why: Battle effects may have
  // already changed this instance's Health by the time we get here).
  const monsterDefBeforeBattle = getInstanceDefinition(workingState, monsterInstanceId);

  // Every "Add Health +N" line below is scoped to THIS combat only - none of
  // them say "permanently" (contrast Belac, which really does keep the
  // Health it gains and so writes applyInstanceOverride directly). They all
  // used to call applyInstanceOverride themselves, reading the instance's
  // CURRENT health and writing back current+N - which meant the bonus stuck
  // to the instance after the fight and compounded every time the same
  // monster was fought again.
  //
  // Brian reported this directly: "Mowtil the thunderstone bearer is broken.
  // He is suppose to add health +1 for each card present. He is an 8, I had
  // 6 cards present so he should have been a 14, but it said he was a 32."
  // His attached transcript has him losing to (and so leaving in the hall)
  // the same Mowtil instance twice: Health 26 on turn 162, then Health 32 on
  // turn 220 - exactly +6, his hand size, stacked onto an already-inflated
  // 26. Same latent bug as Royal Guard's own cross-turn Strength penalty
  // below, and the same shape of fix: track what this combat added so it can
  // be taken back off.
  //
  // Tracked as a running total rather than by snapshotting the "before"
  // health so the revert at the end of this function subtracts only what
  // WE added - any Health change a Battle or Aftermath effect makes in
  // between survives untouched.
  let combatHealthBonus = 0;
  const addCombatHealth = (s, amount) => {
    combatHealthBonus += amount;
    const beforeBonus = getInstanceDefinition(s, monsterInstanceId);
    return applyInstanceOverride(s, monsterInstanceId, { health: (beforeBonus.health || 0) + amount });
  };

  // Caverns of Bane, Mowtil Djinni Lich: "Add Health +1 for each card
  // present." An alwaysActive trait line (not a Battle-timed handler), so it
  // can't go through resolveTimedHandlers above - special-cased here instead
  // (see SPECIAL_CASE_ALWAYS_ACTIVE in effectRegistryCoverage.test.js),
  // applied before the Health re-read below so it's included in `won`.
  if (monsterDefBeforeBattle.id === 'bearer-mowtil-djinni-lich' || monsterDefBeforeBattle.id === 'bearer-convergence-master') {
    const handSize = workingState.players.find((p) => p.id === playerId).hand.length;
    workingState = addCombatHealth(workingState, handSize);
  }

  // Root of Corruption, Horror Incarnate: "Add Health +2 if there is
  // Darkness in this rank." Same reason as Mowtil above - an alwaysActive
  // specialEffects line, not a Battle-timed handler.
  if (monsterDefBeforeBattle.id === 'monster-horror-incarnate' && hasDarknessAtRank(workingState, playerId, hallRankIndex)) {
    workingState = addCombatHealth(workingState, 2);
  }

  // Numenera, Guardian Automaton: "Add Health +4 if a monster is present
  // (in your hand)." Same reason as Horror Incarnate above.
  if (monsterDefBeforeBattle.id === 'monster-guardian-automaton'
    && workingState.players.find((p) => p.id === playerId).hand.some((id) => getInstanceDefinition(workingState, id).type === 'monster')) {
    workingState = addCombatHealth(workingState, 4);
  }

  // Root of Corruption, Royal Guard: "Each hero suffers Strength -N [and
  // Attack -M]." All alwaysActive specialEffects lines (not Battle-timed) -
  // applied once, when that specific monster is fought, mirroring how
  // Packhunter Gnoll/Decay Incarnate's own (Battle/Trophy-timed, registry-
  // handled) "each hero suffers" lines work. Deliberately treats "always
  // active while in the hall" text as scoped to this combat only, rather
  // than building a passive whole-hall aura affecting every player's
  // unrelated fights - see docs/expansion-audit.md's review list.
  //
  // Matt reported this directly: "My regular has strength of only 1 but his
  // strength should be three. His strength was reduced in a past turn but
  // that should not last across turns." This used to call
  // applyInstanceOverride directly, bypassing grantTemporaryStrength's own
  // turnFlags.temporaryStrengthBoostInstanceIds tracking (see its own
  // comment - "Judgement's 'Each hero suffers Strength -2', and others" is
  // literally listed as an intended caller) - with nothing tracking it,
  // endTurn's own revert loop never found it, so "scoped to this combat
  // only" was true in comment only, not in the code.
  const ROYAL_GUARD_STRENGTH_PENALTY = {
    'monster-guardsman': 6, 'monster-weapons-master': 2, 'monster-chiurgeon': 2, 'monster-guard-captain': 2,
  };
  const strengthPenalty = ROYAL_GUARD_STRENGTH_PENALTY[monsterDefBeforeBattle.id];
  if (strengthPenalty) {
    for (const heroId of workingState.players.find((p) => p.id === playerId).hand) {
      const def = getInstanceDefinition(workingState, heroId);
      if (def.strength === undefined || def.strength === null) continue;
      workingState = grantTemporaryStrength(workingState, playerId, heroId, -strengthPenalty);
    }
  }
  if (monsterDefBeforeBattle.id === 'monster-guard-captain') {
    const heroCount = workingState.players.find((p) => p.id === playerId).hand
      .filter((id) => isHeroLike(getInstanceDefinition(workingState, id))).length;
    if (heroCount) {
      workingState = {
        ...workingState,
        players: workingState.players.map((p) => (p.id === playerId ? { ...p, turnFlags: { ...p.turnFlags, bonusTotalAttack: p.turnFlags.bonusTotalAttack - heroCount } } : p)),
      };
    }
  }

  // Worlds Collide, Skywyrm Overlord: "specialEffects: Ignore 1 hero for
  // each class present." Another alwaysActive specialEffects line (not
  // Battle-timed text), same "scoped to this combat only" treatment as
  // Royal Guard just above. Unlike a normal queueHandChoice (async - doesn't
  // resolve until a later, separate player action), this needs to actually
  // take effect WITHIN this same combat, both to void the ignored heroes'
  // own attack contribution for this fight and so its own Aftermath
  // ("Destroy each hero that was ignored," effectRegistry/monsters.js) has
  // something to read back - so it's marked SYNCHRONOUSLY via
  // markInstanceIgnored, auto-picking the lowest-Strength hero of each
  // class present (no player choice UI for a one-off specialEffects pick,
  // same "Do What You Can" auto-pick convention as the Undead Spectral
  // cost payments below).
  // Card was mistitled "Skywyrm Scavenger" in source data (a WC audit,
  // 2026-07-22, fix) - it's actually a different, separate card in the same
  // Dragon Humanoid group; the id here was updated to match (see
  // docs/expansion-audit.md for the full writeup, including the sibling
  // "Skywyrm Scavanger" typo this untangles).
  if (monsterDefBeforeBattle.id === 'monster-skywyrm-overlord') {
    const SKYWYRM_HERO_CLASSES = ['Fighter', 'Wizard', 'Cleric', 'Thief', 'Ranger'];
    for (const heroClass of SKYWYRM_HERO_CLASSES) {
      const candidates = workingState.players.find((p) => p.id === playerId).hand.filter((id) => {
        const def = getInstanceDefinition(workingState, id);
        return isHeroLike(def) && def.traits.includes(heroClass);
      });
      if (!candidates.length) continue;
      const minStrength = Math.min(...candidates.map((id) => getInstanceDefinition(workingState, id).strength || 0));
      const target = candidates.find((id) => (getInstanceDefinition(workingState, id).strength || 0) === minStrength);
      workingState = markInstanceIgnored(workingState, playerId, target);
    }
  }

  // Worlds Collide, The Last Doomknight (Thunderstone Bearer): "Global:
  // Suffer Light -1. Each hero suffers Strength -1. Other monsters add
  // Health +1." A whole-hall-state passive, checked via hall presence (like
  // Steam Torment/Plaguebearer's own Globals above) rather than "when
  // fighting this monster specifically." The Strength -1 half is applied
  // the same scoped-to-this-combat way Royal Guard's own "Each hero suffers
  // Strength -N" is just above (a documented simplification, not a
  // persistent whole-hall aura affecting weapon-equip eligibility outside
  // combat resolution) - see docs/expansion-audit.md. The Light -1 half is
  // folded into lightPenalty below, alongside Murden/Blood Barm's own.
  // applyLastDoomknightHeroStrengthPenalty (effectHelpers.js) is shared
  // with DungeonHall.vue's own "Your Attack" preview, so the number shown
  // before a fight and the number actually used to resolve it can't drift
  // apart again the way they did before (Matt: "It said I had 15 attack
  // but then suddenly I had 12 attack").
  if (anyLastDoomknightInHall(workingState)) {
    workingState = applyLastDoomknightHeroStrengthPenalty(workingState, playerId);
    if (monsterDefBeforeBattle.id !== 'bearer-the-last-doomknight') {
      // Scoped to this combat (addCombatHealth, above) like every other
      // "Add Health" line - this one had the worst version of the compounding
      // bug, since it applied to every OTHER monster in the hall and so crept
      // up by +1 on each of them every single time one was fought.
      workingState = addCombatHealth(workingState, 1);
    }
  }

  // Worlds Collide, Undead Spectral group: "Immune to Physical Attack and
  // Magic Attack unless you [pay a cost] when you select this monster to
  // fight." A per-monster cost, opted into via choices.payUndeadSpectralCost
  // on CHOOSE_MONSTER - paid synchronously here (same reasoning as Skywyrm
  // Scavenger just above: this needs to actually take effect WITHIN this
  // combat, before computeBaseAttack runs, not via an async queued choice
  // resolved later), auto-picking the lowest-value qualifying target(s)
  // rather than offering a real choice UI for a one-off cost, then setting
  // turnFlags.paidUndeadSpectralImmunity so attackMath.js's
  // UNDEAD_SPECTRAL_IMMUNITY check waives the immunity for this fight.
  const UNDEAD_SPECTRAL_COSTS = {
    'monster-souleater': (s) => {
      // Non-hero cards first (regardless of gold value), heroes only as a
      // last resort, weakest-Strength hero first within that tier (isHeroLike
      // also matches basic-regular, not just named heroes) - same
      // conservative "don't sacrifice a hero to an auto-picked cost" bias as
      // the AI's own effectChoice handling elsewhere, so this auto-pick
      // can't accidentally discard the very hero fighting this same combat
      // ahead of a lower-value hero-like card.
      const hand = [...s.players.find((p) => p.id === playerId).hand].sort((a, b) => {
        const defA = getInstanceDefinition(s, a);
        const defB = getInstanceDefinition(s, b);
        const heroRank = (d) => (isHeroLike(d) ? 1 : 0);
        if (heroRank(defA) !== heroRank(defB)) return heroRank(defA) - heroRank(defB);
        if (heroRank(defA) === 1) return (defA.strength || 0) - (defB.strength || 0);
        return (defA.goldValue || 0) - (defB.goldValue || 0);
      });
      let next = s;
      for (const id of hand.slice(0, 3)) next = discardFromHand(next, playerId, id);
      return next;
    },
    'monster-cruel-apparition': (s) => {
      const heroes = s.players.find((p) => p.id === playerId).hand.filter((id) => isHeroLike(getInstanceDefinition(s, id)));
      if (!heroes.length) return s;
      const min = Math.min(...heroes.map((id) => getInstanceDefinition(s, id).strength || 0));
      const target = heroes.find((id) => (getInstanceDefinition(s, id).strength || 0) === min);
      return destroyFromHand(s, playerId, target);
    },
    'monster-will-o-the-wisp': (s) => {
      const target = s.players.find((p) => p.id === playerId).hand.find((id) => getInstanceDefinition(s, id).traits.includes('Food'));
      return target ? destroyFromHand(s, playerId, target) : s;
    },
    'monster-wight': (s) => ({
      ...s,
      players: s.players.map((p) => (p.id === playerId ? { ...p, xp: Math.max(0, p.xp - 2) } : p)),
    }),
    'monster-devourer': (s) => {
      const target = s.players.find((p) => p.id === playerId).hand.find((id) => getInstanceDefinition(s, id).type === 'monster');
      return target ? destroyFromHand(s, playerId, target) : s;
    },
  };
  if (choices?.payUndeadSpectralCost && UNDEAD_SPECTRAL_COSTS[monsterDefBeforeBattle.id]) {
    workingState = UNDEAD_SPECTRAL_COSTS[monsterDefBeforeBattle.id](workingState);
    workingState = updatePlayerTurnFlags(workingState, playerId, (tf) => ({ ...tf, paidUndeadSpectralImmunity: monsterDefBeforeBattle.id }));
  }

  let monsterDef = getInstanceDefinition(workingState, monsterInstanceId); // re-read: battle effects may have changed its Health

  // computeMonsterLightPenalty (effectHelpers.js) - Murden/Blood Barm/Lord
  // Mortis/Darkness's own "Suffer Light -1", plus Penumbra Fiend/
  // Philethis's negative monster.light, plus the Last Doomknight's own
  // Global. Shared with DungeonHall.vue's "Your Attack" preview so it
  // can't drift out of sync with what a real fight actually computes.
  const lightPenalty = computeMonsterLightPenalty(workingState, monsterDef);
  const totalLight = Math.max(0, computeTotalLight(workingState, playerId, hallRankIndex) - lightPenalty);

  // Numenera, Lavrik-ca: "Reduce this card's Health by 2 for each point of
  // Light present." (specialEffects, alwaysActive.) Uses the Light total
  // AFTER the penalty above, same as everything else this fight sees.
  if (monsterDef.id === 'monster-lavrik-ca') {
    workingState = applyInstanceOverride(workingState, monsterInstanceId, { health: Math.max(0, (monsterDef.health || 0) - 2 * totalLight) });
    monsterDef = getInstanceDefinition(workingState, monsterInstanceId);
  }

  const { physical: rawPhysical, magic: rawMagic } = computeBaseAttack(workingState, playerId, {
    targetMonsterGroup: monsterDef.group, monsterTraits: monsterDef.traits, hallRankIndex, totalLight, monsterVP: monsterDef.points, monsterXP: monsterDef.xp, monsterId: monsterDef.id,
    monsterHasBattle: monsterDef.abilityText.battle.length > 0, monsterHasAftermath: monsterDef.abilityText.aftermath.length > 0,
  });
  // Worlds Collide, Noxious Slag/Ghost: "Halve Magic Attack (round down)." /
  // "Halve Physical Attack (round down) without Magic Attack present."
  // Unlike Tormentor/Succubus's own HALVE_UNLESS below (which halves the
  // fully-combined total), these halve one SPECIFIC subtotal before it's
  // ever combined - a new, narrower operation, applied right where the
  // subtotals are freshly split out.
  const magic = monsterDef.id === 'monster-noxious-slag' ? Math.floor(rawMagic / 2) : rawMagic;
  const physical = (monsterDef.id === 'monster-ghost' && rawMagic === 0) ? Math.floor(rawPhysical / 2) : rawPhysical;
  // Into the Abyss, Blind Grandmaster/Shihan: "Ignore Darkness penalties."
  // An alwaysActive passive trait (not a Dungeon-ability grant like Tuath's
  // own ignoreDarknessPenalty turnFlag) - true whenever either is present in
  // hand, checked directly here alongside the turnFlag rather than routing
  // through it.
  const activePlayerForDarkness = workingState.players.find((p) => p.id === playerId);
  const hasBlindIgnoreDarkness = activePlayerForDarkness.hand.some((id) => {
    const cardId = getInstanceDefinition(workingState, id).id;
    return cardId === 'hero-blind-l3' || cardId === 'hero-blind-l4';
  });
  const darknessPenalty = computeDarknessPenalty({
    boardSide: workingState.meta.boardSide,
    hallRankIndex,
    totalLight,
    hardDarkness: workingState.meta.hardDarkness,
    ignoreDarkness: activePlayerForDarkness.turnFlags.ignoreDarknessPenalty || hasBlindIgnoreDarkness,
  });
  const bonusTotalAttack = workingState.players.find((p) => p.id === playerId).turnFlags.bonusTotalAttack || 0;
  const rawTotalAttack = Math.max(0, physical + magic - darknessPenalty + bonusTotalAttack);
  // Into the Abyss, Tormentor/Succubus: "Halve Total Attack Value (round
  // down) unless a weapon is equipped / unless Magic Attack is present." A
  // genuinely new operation on the final computed total (every existing
  // monster-conditional effect either ADDS/subtracts a known amount or
  // voids one already-computed subtotal before this point - nothing
  // multiplies/halves the combined total) - applied here, right alongside
  // Lavrik-ca's own "reduce to 0" override, since both need the fully
  // assembled totalAttack (after Darkness/bonusTotalAttack) to act on.
  const HALVE_UNLESS = {
    'monster-tormentor': () => Object.keys(workingState.players.find((p) => p.id === playerId).equippedWeapons).length > 0,
    'monster-succubus': () => magic > 0,
    'monster-uyril-unending': () => magic > 0, // Worlds Collide: "Halve Total Attack Value (round down) unless Magic Attack is present"
  };
  const halveCondition = HALVE_UNLESS[monsterDef.id];
  const halved = halveCondition && !halveCondition() ? Math.floor(rawTotalAttack / 2) : rawTotalAttack;
  // Numenera, Lavrik-ca: "Reduce your Total Attack Value to 0."
  // (specialEffects, alwaysActive - overrides everything computed above.)
  const totalAttack = monsterDef.id === 'monster-lavrik-ca' ? 0 : halved;
  // Numenera, Ultraterrestrial group: "You cannot defeat this card with N
  // or higher Total Attack Value" / "...unless exactly N" - a defeat gate
  // independent of the normal Health comparison.
  const defeatCondition = DEFEAT_CONDITIONS[monsterDef.id];
  const won = totalAttack >= (monsterDef.health || 0) && (!defeatCondition || defeatCondition(totalAttack));
  const isGuardian = monsterDef.traits.includes('Guardian');
  const isThunderstoneBearer = monsterDef.traits.includes('Thunderstone Bearer');

  // Icon of Inversion: any Battle-timed handlers queued above (see
  // resolveTimedHandlers) actually run now, right alongside the fought
  // monster's own Aftermath resolution - win/loss (`won`, just above) was
  // already decided without them.
  workingState = flushDeferredBattleEffects(workingState, playerId);

  workingState = resolveTimedHandlers(workingState, monsterDef, 'aftermath', {
    playerId, instanceId: monsterInstanceId, monsterInstanceId, hallRankIndex, won, totalAttack, monsterHealth: monsterDef.health || 0,
  });
  workingState = resolveHandCardAftermaths(workingState, {
    playerId, monsterInstanceId, hallRankIndex, won, totalAttack, monsterHealth: monsterDef.health || 0,
  });

  let { players } = workingState;
  let dungeonHall = [...workingState.dungeonHall];
  let { dungeonDeck } = workingState;
  let { winnerId, endTrigger } = workingState.meta;

  // A win might have already removed/relocated this monster via its own
  // Aftermath effect (e.g. Phoenix-style "return to top of deck" patterns
  // don't exist in ToR's Aftermath texts, but Battle effects like Drakeclan
  // Laird's chain-kill do remove OTHER hall instances - re-check presence).
  // For an assigned fight (Mind Control), "still at rank" means "still in
  // the assigned-monster zone" - it was never in dungeonHall to begin with.
  const stillAtRank = isAssignedFight
    ? players.find((p) => p.id === playerId).inPlayInstanceIds.includes(monsterInstanceId)
    : dungeonHall[hallRankIndex] === monsterInstanceId;

  if (won && stillAtRank) {
    if (isAssignedFight) {
      players = players.map((p) => (p.id === playerId
        ? { ...p, inPlayInstanceIds: p.inPlayInstanceIds.filter((id) => id !== monsterInstanceId) }
        : p));
    } else {
      dungeonHall[hallRankIndex] = null;
    }
    const isValidRedirectTarget = choices?.discardToPlayerId && players.some((p) => p.id === choices.discardToPlayerId);
    const discardToPlayerId = (DEFEAT_REDIRECTABLE.has(monsterDef.id) && isValidRedirectTarget)
      ? choices.discardToPlayerId
      : playerId;
    players = players.map((p) => (p.id === discardToPlayerId ? { ...p, discard: [...p.discard, monsterInstanceId] } : p));
    // Matt: "I usually include multiple thunderstone bearers in my deck...
    // the game should only end when all of them have been defeated." This
    // used to end the game on the FIRST bearer defeated regardless of
    // bearerCount (createGameState.js's own house rule) - only the plain,
    // default 1-bearer case ever exercised this path before, so nothing
    // caught it. A not-yet-fought bearer can only be sitting in the
    // dungeon deck (not yet drawn) or the hall (drawn, not yet fought) -
    // it's never in a player's hand/discard under normal rules the way an
    // already-resolved one is.
    if (isThunderstoneBearer) {
      const anotherBearerRemains = dungeonDeck.some((id) => getInstanceDefinition(workingState, id).traits.includes('Thunderstone Bearer'))
        || dungeonHall.some((id) => id && getInstanceDefinition(workingState, id).traits.includes('Thunderstone Bearer'));
      if (!anotherBearerRemains) {
        endTrigger = 'defeated';
        winnerId = playerId;
      }
    }
    // e.g. Drakeclan Laird: "If you defeat this card, you also defeat each
    // other kobold in the hall. You do not get XP for those kobolds."
    if (monsterDef.chainKillGroup) {
      const chainKilledIds = dungeonHall
        .map((id, rank) => ({ id, rank }))
        .filter(({ id }) => id && getInstanceDefinition(workingState, id).group === monsterDef.chainKillGroup);
      for (const { id, rank } of chainKilledIds) {
        dungeonHall[rank] = null;
        players = players.map((p) => (p.id === playerId ? { ...p, discard: [...p.discard, id] } : p));
      }
    }
  } else if (won) {
    // Already removed by its own Battle/Aftermath effect - still award XP/discard bookkeeping.
    players = players.map((p) => (p.id === playerId
      ? { ...p, discard: [...p.discard, monsterInstanceId] }
      : p));
  } else if (isAssignedFight && stillAtRank) {
    // Root of Corruption, Mind Control: "If the monster is not defeated,
    // place it on the bottom of the dungeon deck." Unconditional - the
    // Guardian/Troll "never retreats from the hall" exception just below
    // doesn't apply here; this monster was never in the hall to retreat
    // from in the first place.
    players = players.map((p) => (p.id === playerId
      ? { ...p, inPlayInstanceIds: p.inPlayInstanceIds.filter((id) => id !== monsterInstanceId) }
      : p));
    dungeonDeck = [...dungeonDeck, monsterInstanceId];
  } else if (isGuardian || STAYS_IN_HALL_ON_LOSS.has(monsterDef.id) || !stillAtRank || anySteamTormentInHall(workingState, dungeonHall)) {
    // Guardians (and Caverns of Bane's Trolls) never retreat; a non-guardian
    // that's already gone from this rank (moved by its own effect) doesn't
    // need retreat handling either; with a Steam Torment anywhere in the
    // hall, NOTHING retreats on a loss regardless of which monster was
    // actually fought.
  } else {
    dungeonHall[hallRankIndex] = null;
    dungeonDeck = [...dungeonDeck, monsterInstanceId];
  }

  let nextState = {
    ...workingState,
    players,
    dungeonHall,
    dungeonDeck,
    meta: {
      ...workingState.meta,
      winnerId,
      endTrigger,
      spoilsWindowOpen: won,
      smugglerReactWindowOpen: !won && hasEligibleSmuggler(workingState, playerId),
    },
  };

  // Matt reported this directly: "When I get XP from Village abilities or
  // from dungeon abilities, I'm getting the color-coded XP that we would
  // expect but when I defeat a monster, I'm given XP and the total number
  // of XP goes up but the specific color numbers do not." This used to
  // mutate p.xp directly (above, in the players.map() calls this replaced)
  // instead of going through grantXp - the one function that ALSO updates
  // xpTokens (the colored breakdown, state/xpTokens.js's drawXpTokens),
  // so the raw total was right but the colors silently fell out of sync.
  if (won) nextState = grantXp(nextState, playerId, monsterDef.xp || 0);

  // Plaguebearer's Global (see anyPlaguebearerInHall above): only the
  // directly-fought monster's own defeat is checked here - Drakeclan
  // Laird-style chain kills are NOT separately counted, a documented scope
  // limitation (see docs/expansion-audit.md).
  if (won && anyPlaguebearerInHall(nextState, nextState.dungeonHall)) {
    for (const p of nextState.players) nextState = gainCurse(nextState, p.id);
  }

  // An assigned fight never touches dungeonHall at all - nothing to refill.
  if (!isAssignedFight && nextState.dungeonHall[hallRankIndex] === null) {
    nextState = compactAndRefill(nextState, { playerId });
  }

  // e.g. Aird L3: a borrowed hero returns to its owner's discard pile right
  // after this combat resolves, even if it was destroyed in the meantime.
  const actingPlayer = nextState.players.find((p) => p.id === playerId);
  if (actingPlayer.turnFlags.pendingHeroReturns.length) {
    nextState = {
      ...nextState,
      players: nextState.players.map((p) => {
        if (p.id !== playerId) return p;
        let hand = p.hand;
        const byOwner = {};
        for (const { instanceId, ownerId } of p.turnFlags.pendingHeroReturns) {
          if (!hand.includes(instanceId)) continue; // already gone (destroyed, discarded, etc.)
          hand = hand.filter((id) => id !== instanceId);
          (byOwner[ownerId] ||= []).push(instanceId);
        }
        return { ...p, hand, turnFlags: { ...p.turnFlags, pendingHeroReturns: [] } };
      }),
    };
    for (const { instanceId, ownerId } of actingPlayer.turnFlags.pendingHeroReturns) {
      nextState = {
        ...nextState,
        players: nextState.players.map((p) => (p.id === ownerId && !p.discard.includes(instanceId)
          ? { ...p, discard: [...p.discard, instanceId] }
          : p)),
      };
    }
  }

  // Matt's house rule: "I never play by the rule that if the thunderstone
  // bearer reaches rank 1 the game ends. The game only ends when the
  // thunderstone bearer is defeated." bearerNeverEscapes skips this whole
  // check - the bearer can then sit at rank 1 indefinitely; the game still
  // ends normally via the 'defeated' trigger below (bearer actually fought
  // and beaten) or an unrelated elimination trigger.
  if (!nextState.meta.endTrigger && !nextState.meta.bearerNeverEscapes) {
    const rank1InstanceId = nextState.dungeonHall[0];
    const rank1Def = rank1InstanceId ? getInstanceDefinition(nextState, rank1InstanceId) : null;
    if (rank1Def && rank1Def.traits.includes('Thunderstone Bearer')) {
      nextState = { ...nextState, meta: { ...nextState.meta, endTrigger: 'escaped' } };
    }
  }

  // The fought monster's own Aftermath may have queued a pending choice
  // (e.g. "destroy 1 hero") whose candidates a LATER, un-queued hand-card
  // Aftermath (resolveHandCardAftermaths above - e.g. Disowned Bloodrager's
  // "destroy all other heroes") then destroyed directly, before anyone got
  // a chance to resolve that earlier choice. dropDeadEntries normally only
  // re-checks the queue head after each RESOLVE_HAND_CHOICE dispatch: swept
  // here too so a dispatch that queues AND invalidates a choice within the
  // same combat resolution doesn't hand back a dead entry with 0 legal
  // targets (surfaced by AI self-play: "That card is not a valid choice").
  nextState = dropDeadEntries(nextState);

  // Take back the scoped-to-this-combat Health bonuses added at the top of
  // this function (see addCombatHealth's own comment for the bug this
  // fixes). Done last, so anything that reads Health mid-fight - `won`, the
  // Aftermath handlers, the `monsterDef` snapshot returned in `result` and
  // logged by describeCombatOutcome - all still see the boosted number,
  // which IS the Health the fight was actually resolved against. Applies on
  // a win too: a defeated monster becomes a trophy, and a trophy shouldn't
  // carry an inflated Health either. The instance can be gone entirely by
  // now (destroyed outright by an Aftermath), hence the guard.
  if (combatHealthBonus && nextState.instances[monsterInstanceId]) {
    const afterCombat = getInstanceDefinition(nextState, monsterInstanceId);
    nextState = applyInstanceOverride(nextState, monsterInstanceId, {
      health: (afterCombat.health || 0) - combatHealthBonus,
    });
  }

  // hallRankIndex here is the FINAL rank (post-Sathosh-move, if it moved) -
  // applyCombatConclusion below stashes this into meta.lastCombatResult
  // instead of a caller-side copy, since a reassignment inside this function
  // is a local rebinding that never propagates back to a plain number
  // parameter.
  return {
    state: nextState, result: {
      won, totalAttack, physical, magic, darknessPenalty, monsterDef, hallRankIndex,
    },
  };
}

// Matt: "It seems like the battle is being resolved before battle effects
// are applied sometimes. When I fought the Smokeplume Drake, it said I won,
// applied its aftermath effect of destroying the hero with the highest
// strength, and then only after all that did it ask me which two cards I
// would discard for the battle effect. The sequence should be to resolve
// battle effects, then determine who won the combat, then to apply the
// aftermath effects." Root cause: resolveCombatContinuePhase above (win/loss
// + Aftermath) used to run unconditionally, synchronously, right after
// Battle - but a Battle effect needing player input (queueHandChoice et al.,
// e.g. Smokeplume Drake's "Discard 2 cards") only QUEUES that request; it
// doesn't block anything on its own. So win/loss and Aftermath (Smokeplume's
// own "destroy the highest-Strength hero") ran against the hand as it stood
// BEFORE the player answered the Battle prompt.
//
// Fixed by pausing here: if the Battle phase left anything in
// meta.pendingHandChoices, this stashes the Continue phase's own context on
// meta.pendingCombatContinuation and returns early with result: null,
// instead of running Continue immediately. resumeCombatIfReady (below) runs
// Continue for real once that queue actually drains. This is safe without
// tagging which queue entries belong to this fight: actions/index.js's
// dispatch() already refuses every action except RESOLVE_HAND_CHOICE (and
// its siblings) while pendingHandChoices is non-empty, so CHOOSE_MONSTER -
// the only caller of this function - can only ever run when the queue was
// already empty; anything in it right after Battle resolves is unambiguously
// this fight's own.
//
// Root of Corruption, Mind Control (effectRegistry/villages.js):
// hallRankIndex === -1 is the sentinel for "fighting a monster assigned
// outside the hall" - see resolveCombatBattlePhase's own isAssignedFight and
// dungeonActions.js's chooseMonster, which is the only caller that ever
// passes -1.
export function resolveCombat(state, args) {
  const { state: afterBattle, ctx } = resolveCombatBattlePhase(state, args);
  if ((afterBattle.meta.pendingHandChoices || []).length) {
    return {
      state: {
        ...afterBattle,
        meta: { ...afterBattle.meta, pendingCombatContinuation: ctx },
      },
      result: null,
    };
  }
  return resolveCombatContinuePhase(afterBattle, ctx);
}

// Called from actions/index.js's dispatch() after every action - a cheap
// no-op check (meta.pendingCombatContinuation is null the overwhelming
// majority of the time) unless a fight is actually paused waiting on a
// Battle-queued choice AND that choice has just been fully answered (queue
// now empty). When both hold, runs the Continue phase for real - win/loss
// and Aftermath now see the hand exactly as the player left it.
export function resumeCombatIfReady(state) {
  const ctx = state.meta.pendingCombatContinuation;
  if (!ctx || (state.meta.pendingHandChoices || []).length) return state;
  const cleared = { ...state, meta: { ...state.meta, pendingCombatContinuation: null } };
  const { state: concluded, result } = resolveCombatContinuePhase(cleared, ctx);
  const withConclusion = applyCombatConclusion(concluded, { playerId: ctx.playerId, result, monsterInstanceId: ctx.monsterInstanceId });
  // The dispatch that actually drained the queue (e.g. an opponent
  // answering Hellhound's own "discard a hero" choice) gets its OWN log
  // line from appendLog as usual - but that dispatch isn't necessarily by
  // the fighting player (ctx.playerId) at all, so its description can't
  // also carry the fight's own outcome. Stashed here (same
  // "reactive consequence follows the action that caused it" mechanism
  // appendLog already uses for Caldera Dragon's own Global discard) so
  // "p1 defeated Hellhound..." still shows up, correctly attributed to
  // p1, right after whatever action actually finished resolving it.
  return {
    ...withConclusion,
    meta: {
      ...withConclusion.meta,
      pendingGlobalLogMessage: { playerId: ctx.playerId, message: describeCombatOutcome(result) },
    },
  };
}

// Shared by resumeCombatIfReady above and actions/index.js's describeAction
// (its CHOOSE_MONSTER case) - the exact same phrasing whether the log line
// lands immediately (the common case: no Battle-queued choice) or later,
// once a paused fight actually concludes.
export function describeCombatOutcome(result) {
  return result.won
    ? `defeated ${result.monsterDef.title} (Attack ${result.totalAttack} vs Health ${result.monsterDef.health}) and gained ${result.monsterDef.xp || 0} XP.`
    : `lost to ${result.monsterDef.title} (Attack ${result.totalAttack} vs Health ${result.monsterDef.health}).`;
}

// The bookkeeping that only makes sense once a fight has ACTUALLY concluded
// (a real `result` in hand): win/loss stats and meta.lastCombatResult (read
// by GameBoard.vue's banner (combatLine) and PlayerDock.vue's post-victory
// Spoils prompt). Shared by dungeonActions.js's chooseMonster (the common
// case - no Battle-queued choice, result is available immediately) and
// resumeCombatIfReady above (the paused case - result only becomes available
// once the queue drains), so the two paths can't drift out of sync.
// foughtMonsterThisTurn/forcedFightObligation-clearing are NOT here - those
// are about commitment to the fight, not its outcome, so chooseMonster
// applies them immediately either way (see its own comment).
export function applyCombatConclusion(state, { playerId, result, monsterInstanceId }) {
  const players = state.players.map((p) => (p.id === playerId
    ? {
      ...p,
      stats: {
        ...p.stats,
        dungeonWins: p.stats.dungeonWins + (result.won ? 1 : 0),
        dungeonLosses: p.stats.dungeonLosses + (result.won ? 0 : 1),
      },
    }
    : p));
  return { ...state, players, meta: { ...state.meta, lastCombatResult: { ...result, monsterInstanceId } } };
}
